import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import { useHlsPlayerUI } from './HlsPlayerUIContext'

/**
 * Seek bar with buffered range indicator and hover time tooltip.
 *
 * Layers (back to front):
 * 1. Track background (white/30)
 * 2. Buffered range (white/50)
 * 3. Played progress (white)
 * 4. Scrubber thumb (visible on hover/seeking)
 * 5. Invisible full-width input[range] for interaction
 * 6. Hover time tooltip
 */
export const HlsSeekBar: React.FC = () => {
  const { currentTime, duration, buffered, controller } = useHlsPlayerUI()
  const [isSeeking, setIsSeeking] = useState(false)
  const [seekTime, setSeekTime] = useState(0)
  const [isHovering, setIsHovering] = useState(false)
  const [hoverPercent, setHoverPercent] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)

  const displayTime = isSeeking ? seekTime : currentTime
  const progressPercent = duration > 0 ? (displayTime / duration) * 100 : 0
  const bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 0

  const handlePointerDown = () => {
    setIsSeeking(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = Number(e.target.value)
    setSeekTime(newTime)
    controller?.seek(newTime)
  }

  const handlePointerUp = () => {
    if (isSeeking) {
      controller?.seek(seekTime)
      setIsSeeking(false)
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!trackRef.current || duration <= 0) return
    const rect = trackRef.current.getBoundingClientRect()
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100))
    setHoverPercent(percent)
  }

  const hoverTimeSeconds = duration > 0 ? (hoverPercent / 100) * duration : 0
  return (
    <div
      ref={trackRef}
      className="group relative flex w-full cursor-pointer items-center py-2"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseMove={handleMouseMove}
    >
      {/* Track layers */}
      <div className="relative h-1.5 w-full rounded-full bg-white/30">
        {/* Buffered range */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-white/50"
          style={{ width: `${clampPercent(bufferedPercent)}%` }}
        />
        {/* Played progress */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-white"
          style={{ width: `${clampPercent(progressPercent)}%` }}
        />
      </div>

      {/* Scrubber thumb — circle outline */}
      <div
        className={classNames(
          'pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white',
        )}
        style={{ left: `${clampPercent(progressPercent)}%` }}
      />

      {/* Hover time tooltip */}
      {isHovering && !isSeeking && duration > 0 && (
        <div
          className="pointer-events-none absolute -top-8 -translate-x-1/2 rounded bg-black/80 px-1.5 py-0.5 text-xs font-medium text-white"
          style={{ left: `${hoverPercent}%` }}
        >
          {formatSeekTime(hoverTimeSeconds)}
        </div>
      )}

      {/* Invisible range input for accessibility and interaction */}
      <input
        type="range"
        min={0}
        max={duration || 1}
        step="any"
        value={displayTime}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onChange={handleChange}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={displayTime}
      />
    </div>
  )
}

function clampPercent(value: number): number {
  return Math.min(Math.max(value, 0), 100)
}

function formatSeekTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}
